import React from 'react';
import { Alert as AntdAlert } from 'antd';
import { AlertProps as AntdAlertProps } from 'antd/lib/alert';
import classNames from 'classnames';

import { Star, Bulb, SolidCheckCircle, AlertTriangle, SolidAlertCircle } from 'Icons';
import Button, { ButtonProps } from '../Button';

type Color = 'default' | 'success' | 'info' | 'secondary' | 'warning' | 'error';
interface Props extends Omit<AntdAlertProps, 'action'> {
  color?: Color;
  primaryText?: React.ReactNode;
  onPrimary?: ButtonProps['onClick'];
  secondaryText?: React.ReactNode;
  onSecondary?: ButtonProps['onClick'];
}

export type { Props as AlertsProps };

const defaultIcon = {
  default: <Star />,
  info: <Bulb />,
  secondary: <Bulb />,
  success: <SolidCheckCircle />,
  warning: <AlertTriangle />,
  error: <SolidAlertCircle />,
} as Record<Color, JSX.Element>;

export default function Alert({
  message,
  description,
  primaryText,
  secondaryText,
  onPrimary,
  onSecondary,
  closable = false,
  showIcon = false,
  color = 'default',
  className = '',
  ...rest
}: Props): JSX.Element {
  const alertType = color === 'default' || color === 'secondary' ? void 0 : color;
  const icon = showIcon && defaultIcon[color];
  const showBorder = showIcon && !closable;

  return (
    <AntdAlert
      className={classNames(
        'sl-alert tw-border-none tw-text-gray-700 t-px-6 tw-py-4',
        {
          'tw-bg-gray-100': color === 'default',
          'tw-bg-info-100': color === 'info',
          'tw-bg-secondary-100': color === 'secondary',
          'tw-bg-success-100': color === 'success',
          'tw-bg-warning-100': color === 'warning',
          'tw-bg-error-100': color === 'error',
        },
        className
      )}
      closeIcon={<i className="fas fa-times" />}
      type={alertType}
      icon={closable ? icon : null}
      description={
        <div className="tw-flex tw-flex-nowrap">
          <div className="tw-flex-grow tw-min-w-0">
            <div className="tw-font-medium">{message}</div>
            <div className="tw-text-body-3 md:tw-text-body-3">{description}</div>
            {onPrimary && (
              <div
                className={classNames('tw-inline-grid tw-grid-cols-2 tw-gap-4 ', {
                  'tw-mt-8': description,
                  'tw-mt-3': !description,
                })}
              >
                <Button variant="contained" color={color} onClick={onPrimary}>
                  {primaryText}
                </Button>
                <Button variant="outlined" color={color} onClick={onSecondary}>
                  {secondaryText}
                </Button>
              </div>
            )}
          </div>
          {showIcon && <span className="tw-min-w-[20px]">{icon}</span>}
          {showBorder && (
            <div
              className={classNames('tw-w-1 tw-rounded tw-ml-4', {
                'tw-bg-gray': color === 'default',
                'tw-bg-info': color === 'info',
                'tw-bg-secondary': color === 'secondary',
                'tw-bg-success': color === 'success',
                'tw-bg-warning': color === 'warning',
                'tw-bg-error': color === 'error',
              })}
            />
          )}
        </div>
      }
      {...rest}
    />
  );
}
